(ns manage.ad-variant.page.columns
  (:require ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [getGridSingleSelectOperators
                                                GRID_ROOT_GROUP_ID
                                                GridActionsCellItem
                                                isAutogeneratedRow
                                                gridClasses]]
            ["src/svg/wpp/content-actions/edit.svg$default" :as Edit]
            ["src/svg/wpp/content-actions/eye-on.svg$default" :as EyeOn]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            [cljs-bean.core :refer [->clj]]
            [common.queries :refer [use-query]]
            [common.resource.query.product-selector :as product-selector.q]
            [common.resource.query.user :as user.q]
            [common.util :as util]
            [manage.ad-variant.data :as variant-data]
            [manage.ad-variant.widgets :as variant-widgets]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :as hooks :refer [use-subscribe]]))

(defui render-status [{:keys [status variant]}]
  ($ variant-widgets/render-status
     {:status status
      :variant variant}))

(defui render-product-selector-link [{:keys [product-selector-id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {product-selector :data} (use-query
                                  (product-selector.q/product-selector-query
                                   {:advertiser-id advertiser-id
                                    :product-selector-id product-selector-id}))]
    ($ Tooltip
       {:placement :right
        :title product-selector-id}
       ($ Link
          {:noWrap true
           :onClick (fn [^js e]
                      ;; Prevent opening a modal when clicking on a link.
                      (.stopPropagation e)
                      (rf/dispatch [:ocp/open {:id :product-selector/crud
                                               :props {:product-selector-id product-selector-id}}]))
           :sx #js {:cursor "pointer"}}
          (get product-selector :name)))))

(defui ad-type-column-cell [{{{:keys [scheduling]} :row
                              :keys [row rowNode]} :cell-params
                             :keys [show-scheduling data-path]}]
  (let [is-grouped? (some-> rowNode :depth pos?)]
    ($ Stack {:direction "row"
              :alignItems "center"
              :gap 1
              :sx #js {:ml (when is-grouped? 5.5)}}
       (when (and show-scheduling is-grouped? scheduling)
         ($ variant-widgets/scheduling-info-icon scheduling))
       ($ variant-widgets/ad-variant-type-info
          {:ad-variant (cond-> row
                         data-path (get-in data-path))}))))

(def ad-type-column-field "ad-type")

(defn get-ad-type-column [props]
  #js {:field "ad-type"
       :headerName "Template"
       :renderCell (fn [^js params]
                     ($ ad-type-column-cell (assoc props :cell-params (->clj params))))
       :minWidth 220
       :groupable false})

(def product-selector-column
  #js {:field "product"
       :headerName "Product Selector"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (->> (get-in row [:components :urn:lemonpi:artefact:display:content+template :value :dynamic-content-config])
                           (keep (fn [[_ {:keys [source-type config]}]]
                                   (when (= source-type "products")
                                     (:product-selector-id config))))))
       :renderCell (fn [^js params]
                     ($ Stack {:direction "row" :spacing 0.5}
                        (for [selector (.-value params)]
                          ($ render-product-selector-link {:key selector :product-selector-id selector}))))
       :minWidth 180
       :groupable false})

;; We need to store the group expansion state because by default it's lost when updating
;; the rows in the table, more details:
;; https://github.com/mui/mui-x/issues/13064#issuecomment-2106934259
(defhook use-group-expansion-groups [{:keys [^js api-ref negated]}]
  (let [[groups set-groups] (uix/use-state #{})]

    (uix/use-effect
     #(when (some? api-ref)
        (let [handle-expansion-change
              (fn [^js params]
                (let [{:keys [id childrenExpanded]} (->clj params)
                      should-add-key (cond-> childrenExpanded
                                       negated not)]
                  (set-groups (if should-add-key conj disj) id)))]
          (.subscribeEvent (.-current api-ref) "rowExpansionChange" handle-expansion-change)))
     [api-ref negated])
    groups))

(defhook use-group-expanded-fn [{:keys [groups negated]}]
  (uix/use-callback (fn [^js group-node]
                      (let [{:keys [id]} (->clj group-node)]
                        (cond-> (contains? groups id)
                          negated not)))
                    [groups negated]))

(defui identifier-grouping-column-header [{:keys [^js params negated ^js api-ref groups]}]
  (let [current-group-ids (.. api-ref -current (getRowNode GRID_ROOT_GROUP_ID) -children)
        has-expanded-group ((if negated not-any? some) groups current-group-ids)
        toggle-all-row-expansion
        (uix/use-callback
         (fn []
           (doseq [group-id current-group-ids]
             (.. api-ref -current (setRowChildrenExpansion group-id
                                                           (not has-expanded-group)))))
         [api-ref has-expanded-group current-group-ids])]
    ($ Stack
       {:direction "row"
        :alignItems "center"
        :gap 1
        :sx #js {:overflow "hidden"}}
       ($ IconButton
          {:size "small"
           :onClick (fn [e]
                      (.stopPropagation e)
                      (toggle-all-row-expansion))
           :sx #js {:fontSize "1.575rem"}}
          ($ Icon
             {:color "default"
              :sx #js {:fontSize "inherit"}}
             (if has-expanded-group
               "keyboard_arrow_down"
               "keyboard_arrow_right")))
       ($ Typography
          {:noWrap true
           :fontWeight 500
           :class (.-columnHeaderTitle gridClasses)}
          (.. params -colDef -headerName)))))

(defhook use-identifier-grouping-column-props [{:keys [api-ref negated]}]
  (let [groups (use-group-expansion-groups {:api-ref api-ref :negated negated})

        group-expanded-fn (use-group-expanded-fn {:groups groups
                                                  :negated negated})

        groups-ref (uix/use-ref groups)
        grouping-col-def (uix/use-memo
                          (fn [] #js {:minWidth 160
                                      :headerName "Identifier"
                                      :flex 3
                                      :hideable false
                                      :leafField ad-type-column-field
                                      :renderHeader (fn [params]
                                                      ($ identifier-grouping-column-header
                                                         {:params params
                                                          :api-ref api-ref
                                                          :negated negated
                                                          :groups @groups-ref}))})
                          [api-ref negated])]
    ;; Update ref when groups change without causing `grouping-col-def` to change
    ;; DataGrid has a bug: when there's a pinned column in the table and `grouping-col-def`
    ;; changes on every group toggle the table layout falls apart
    (uix/use-effect (fn [] (reset! groups-ref groups)) [groups])
    {:groupingColDef grouping-col-def
     :expanded-groups-state groups
     :isGroupExpandedByDefault group-expanded-fn}))

(defui identifier-column-cell [{:keys [value]}]
  ($ Typography {:component "span" :noWrap true} value))

(def identifier-column
  #js {:type "string"
       :field "contentId"
       :headerName "Identifier"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (:content-id row))
       :groupingValueGetter (fn [value row ^js column ^js apiRef]
                              (.. column (valueGetter value row column apiRef)))
       :renderCell (fn [^js params] ($ identifier-column-cell (->clj params)))
       :minWidth 140
       :flex 3})

(def revision-id-column
  #js {:type "number"
       :field "revision"
       :headerName "Revision ID"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (:revision row))
       :minWidth 100
       :flex 1
       :groupable false})

(def score-column
  #js {:type "number"
       :field "score"
       :headerName "Predictive score"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (variant-data/get-create-lift-score row))
       :renderCell (fn [^js params]
                     (when (.-value params)
                       ($ variant-widgets/predictive-score-chip {:score (.-value params)})))
       :minWidth 100
       :flex 1
       :groupable false})

(def internal-qc-column
  #js {:field "internalReviewStatus"
       :headerName "Internal QC"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      {:approval-state (:approval row)
                       :review-users (:review-users row)})
       :renderCell (fn [^js params]
                     (when (not= (:approval-state (.-value params)) "draft")
                       ($ variant-widgets/internal-qc-labels-with-icons
                          {:review-users (:review-users (.-value params))})))
       :minWidth 150
       :flex 1
       :sortable false
       :filterable false
       :groupable false})

(def last-modified-column
  #js {:type "dateTime"
       :field "createdAt"
       :headerName "Last modified"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (when-let [ts (:created-at row)]
                        (js/Date. ts)))
       :renderCell (fn [^js params]
                     (when-let [datetime (.-value params)]
                       ($ Tooltip
                          {:placement :right
                           :title (util/format-iso-string datetime)}
                          ($ :span (util/human-readable-relative-time datetime)))))
       :filterable false
       :groupable false})

(def simple-status-column
  #js {:type "singleSelect"
       :field "status"
       :headerName "Status"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (:ad-variant-status row))
       :renderCell (fn [^js params]
                     ($ variant-widgets/status-chip
                        {:status (.-value params)}))
       :minWidth 160
       :flex 1
       :filterable false
       :groupable false})

(def render-status-column
  #js {:type "singleSelect"
       :field "status"
       :headerName "Status"
       :valueGetter (fn [_value row ^js _column ^js _apiRef]
                      (:ad-variant-status row))
       :valueOptions (->> variant-data/status
                          (map (fn [[value {label :title}]]
                                 {:value value :label label}))
                          clj->js)
       :filterOperators (.filter (getGridSingleSelectOperators) #(= (.-value %) "isAnyOf"))
       :renderCell (fn [^js params]
                     (when (not= (.. params -rowNode -type) "group")
                       ($ render-status
                          {:status (.-value params)
                           :variant (.-row params)})))
       :minWidth 160
       :flex 1
       :groupable false})

(defui preview-button [{:keys [row]}]
  (let [{me :data} (use-query (user.q/user-query))]
    ($ GridActionsCellItem
       {:icon ($ SvgIcon {:component EyeOn
                          :inheritViewBox true})
        :onClick (fn [e]
                   (let [uuid (:uuid row)
                         user-reviewer? (and (boolean (some (fn [reviewer]
                                                              (= (:user-request-mapping/user-id reviewer) (:id me)))
                                                            (:review-users row)))
                                             (not= "draft" (:approval row)))]
                     (.stopPropagation e)
                     (rf/dispatch [:manage.ad-variant-preview.db/open-preview #{uuid} user-reviewer?])))})))

(defn get-schedule-action-cell-item [{:keys [row read-only]}]
  ($ GridActionsCellItem
     {:icon ($ SvgIcon {:component Calendar
                        :inheritViewBox true})
      :onClick #(rf/dispatch [:modal/open
                              {:id :ad-variants/schedule-modal
                               :props {:ad-variants [row] :read-only read-only}}])}))

(defn actions-column [manual-upload-enabled? ad-variant-scheduling-enabled?]
  #js {:field "actions"
       :type "actions"
       :getActions (fn [^js params]
                     (let [row (.-row params)]
                       (if-not (isAutogeneratedRow row)
                         (cond-> [($ preview-button {:row row})]
                           (and manual-upload-enabled? (nil? (:feed-id row)))
                           (conj ($ GridActionsCellItem
                                    {:icon ($ SvgIcon {:component Edit
                                                       :inheritViewBox true})
                                     :onClick #(rf/dispatch [:modal/open {:id :ad-variants/manual-upload-modal
                                                                          :props {:initial-ad row}}])}))

                           (and manual-upload-enabled? (nil? (:feed-id row))
                                ad-variant-scheduling-enabled?)
                           (conj (get-schedule-action-cell-item {:row row}))

                           (and ad-variant-scheduling-enabled? (some? (:feed-id row))
                                (seq (get-in row [:scheduling :timezone])))
                           (conj (get-schedule-action-cell-item {:row row :read-only true}))

                           true
                           into-array)
                         #js [])))
       :flex 0})

(def width-column
  #js {:field "width"})

(def height-column
  #js {:field "height"})

(defn basic-columns [show-internal-review-column has-product-selector? create-lift-enabled manual-upload-enabled? ad-variant-scheduling-enabled?]
  (->> [identifier-column
        (get-ad-type-column {:show-scheduling false})
        (when has-product-selector?
          product-selector-column)
        revision-id-column
        last-modified-column
        (when show-internal-review-column
          internal-qc-column)
        render-status-column
        (when create-lift-enabled
          score-column)
        (actions-column manual-upload-enabled? ad-variant-scheduling-enabled?)
        width-column
        height-column]
       (remove nil?)
       into-array))

(def field->sort-field
  {(.-field identifier-column) "content-id"
   ;; Server sorting is disabled for these columns
   ;; These will be sorted on FE within the group
   ad-type-column-field nil
   (.-field last-modified-column) nil
   (.-field simple-status-column) nil
   (.-field revision-id-column) nil})

(def field->resolver
  {(.-field identifier-column) "content-id"
   (.-field width-column) "dimensions"
   (.-field height-column) "dimensions"
   ad-type-column-field "ad-type"})

(def field->column
  {(.-field identifier-column) "content-id"
   ad-type-column-cell "ad-type"
   (.-field render-status-column) "ad-variant-status"
   (.-field simple-status-column) "ad-variant-status"})
